import { useAchievementTypes, useReduxState } from 'hooks';
import {
  Badge,
  EmptyIndicator,
  FlexContainer,
  ModalButton
} from 'components/common';
import {
  AchievementCompletionItemsCleared,
  AchievementCompletionsFillTemplateConversation,
  AchievementCompletionsFillTemplateTasklist,
  AchievementGroupsOfUsersGroupFillTemplate
} from '.';

import type { Achievement, AchievementProgress } from 'shared-types';

interface Props {
  authorized: boolean;
  cleared?: string;
  confidential?: boolean;
  confidentialShowBadge?: boolean;
  items: AchievementProgress['template'];
  renderInModal?: boolean;
  renderInModalProps?: React.ComponentProps<typeof ModalButton>;
  targetUserId: number;
  type: Achievement['type'];
}

const AchievementCompletionItemsRender = ({
  authorized,
  cleared,
  confidential = false,
  confidentialShowBadge = true,
  items,
  renderInModal = false,
  renderInModalProps,
  targetUserId,
  type
}: Props) => {
  const { messages } = useReduxState();

  const { icon, templateHeader } = useAchievementTypes({ type });

  const Element = {
    conversation: AchievementCompletionsFillTemplateConversation,
    'group-conversation': AchievementGroupsOfUsersGroupFillTemplate,
    tasklist: AchievementCompletionsFillTemplateTasklist
  }[type];

  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    renderInModal ? (
      <FlexContainer style={{ alignItems: 'center', gap: 5 }}>
        {items.length ? (
          <ModalButton
            category="lighter"
            icon={icon}
            modalContentStyle={{ padding: 0 }}
            modalProps={{
              closeButton: true,
              title: templateHeader,
              width: 'wide'
            }}
            size="small"
            text={templateHeader}
            {...renderInModalProps}
          >
            {children}
          </ModalButton>
        ) : null}

        {confidential && confidentialShowBadge ? (
          <Badge
            category="error-light"
            icon="lock"
            isRounded={true}
            size="small"
            style={{ padding: '2px 7px 3px', height: 22, width: 22 }}
            tooltip={messages.commonConfidential}
          />
        ) : null}
      </FlexContainer>
    ) : (
      <>{children}</>
    );

  return !Element ? null : (
    <Wrapper>
      {cleared ? (
        <AchievementCompletionItemsCleared date={cleared} type={type} />
      ) : authorized ? (
        (items || []).map((item, i) => (
          // The template items are always read-only in this component's context
          <Element
            key={item.id}
            adminMode={true}
            canEdit={false}
            completionId={null}
            item={item}
            itemIndex={i + 1}
            targetUserId={targetUserId}
          />
        ))
      ) : (
        <EmptyIndicator
          description={messages.achievementCompletionConfidentialInfo}
          icon="lock"
          title={messages.commonConfidential}
        />
      )}
    </Wrapper>
  );
};

export default AchievementCompletionItemsRender;
